import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SupabaseClientService } from './db/supabase-client';
import {
  WorkflowJob,
  WorkflowJobStatus,
  WorkflowJobsQueryParams,
  CreateWorkflowJobPayload,
  WorkflowJobWithDetails,
} from '@models/workflow.types';

@Injectable({ providedIn: 'root' })
export class WorkflowJobsService {
  private static readonly TABLE = 'workflow_jobs';

  constructor(private supabaseClient: SupabaseClientService) { }

  /**
   * List workflow jobs with optional filtering
   */
  list(params: WorkflowJobsQueryParams = {}): Observable<{ data: WorkflowJobWithDetails[], count: number }> {
    const {
      workflow_id,
      profile_id,
      status,
      created_by,
      limit = 100,
      offset = 0,
      order_by = 'started_at',
      order_direction = 'desc'
    } = params;

    return from(
      (async () => {
        let query = this.supabaseClient.client
          .from(WorkflowJobsService.TABLE)
          .select(`
            *,
            workflow:workflows (
              id,
              title,
              prompt,
              description
            ),
            latest_run:workflow_job_runs!latest_run_id (
              duration,
              started_at,
              agent_id,
              agent:agents!agent_id (
                name
              )
            )
          `, { count: 'exact' });

        if (order_by === 'started_at') {
          query = query
            .order('latest_run(started_at)', { ascending: order_direction === 'asc', nullsFirst: order_direction !== 'asc' })
            .order('created_at', { ascending: false });
        } else {
          query = query.order(order_by, { ascending: order_direction === 'asc' });
        }

        query = query.range(offset, Math.max(offset, offset + limit - 1));

        if (workflow_id) {
          query = query.eq('workflow_id', workflow_id);
        }
        if (profile_id) {
          query = query.eq('profile_id', profile_id);
        }
        if (status) {
          query = query.eq('latest_status', status);
        }
        if (created_by) {
          query = query.eq('created_by', created_by);
        }

        const { data, error, count } = await query;
        if (error) throw error;
        return {
          data: (data as WorkflowJobWithDetails[]) ?? [],
          count: count ?? 0
        };
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load workflow jobs:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get workflow job by ID with full details (same structure as list$)
   * Used for realtime updates to fetch complete data with relations
   */
  getByIdWithDetails(id: string): Observable<WorkflowJobWithDetails | null> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobsService.TABLE)
          .select(`
            *,
            workflow:workflows (
              id,
              title,
              prompt,
              description
            ),
            latest_run:workflow_job_runs!latest_run_id (
              duration,
              started_at,
              agent_id,
              agent:agents!agent_id (
                name
              )
            )
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        return data as WorkflowJobWithDetails;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to load workflow job details:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Subscribe to real-time changes
   */
  subscribeToChanges(): Observable<any> {
    return this.supabaseClient.subscribeToTable(WorkflowJobsService.TABLE, '*');
  }

  /**
   * Create new workflow job
   */
  create(payload: CreateWorkflowJobPayload): Observable<WorkflowJob> {
    return from(
      (async () => {
        // Get current user ID
        const { data: { user } } = await this.supabaseClient.client.auth.getUser();
        const userId = user?.id || null;

        const jobData = {
          workflow_id: payload.workflow_id,
          profile_id: payload.profile_id,
          parameters: payload.parameters,
          allowed_agents: payload.allowed_agents,
          latest_status: 'pending' as WorkflowJobStatus,
          created_at: new Date().toISOString(),
          created_by: userId
        };

        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobsService.TABLE)
          .insert(jobData)
          .select()
          .single();

        if (error) throw error;
        return data as WorkflowJob;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to create workflow job:', error);
        return throwError(() => error);
      })
    );
  }

  updateWorkflowJob(id: string, data: Partial<WorkflowJob>): Observable<WorkflowJob[]> {
    return this.supabaseClient.update<WorkflowJob>(
      WorkflowJobsService.TABLE,
      data,
      { id }
    );
  }

  /**
   * Update workflow job status
   */
  updateStatus(id: string, status: WorkflowJobStatus, error?: string | null): Observable<WorkflowJob[]> {
    const updates: Partial<WorkflowJob> = {
      latest_status: status
    };

    if (error !== undefined) {
      updates.error = error;
    }

    if (status === 'completed' || status === 'failed') {
      updates.heartbeat_at = null;
    }

    return this.updateWorkflowJob(id, updates)
  }

  /**
   * Try to cancel job if it's still pending
   * Returns true if job was cancelled (was pending), false if job was already taken by worker
   */
  tryCancelPendingJob(jobId: string): Observable<boolean> {
    return from(
      (async () => {
        const { data, error } = await this.supabaseClient.client
          .from(WorkflowJobsService.TABLE)
          .update({
            latest_status: 'cancelled',
            error: 'Cancelled by user'
          })
          .eq('id', jobId)
          .eq('latest_status', 'pending')
          .select();

        if (error) throw error;

        return data && data.length > 0;
      })()
    ).pipe(
      catchError((error) => {
        console.error('Failed to cancel pending job:', error);
        return throwError(() => error);
      })
    );
  }
}
